import React from 'react';
import { Card, Text, Heading, Loading } from '../core';
import { Table } from '../core/Table';
import { AdminBadges } from './AdminBadges';
import { Stats } from '../Stats';
import {
  fromNow,
  secDuration,
  getCustomVaultName,
  useVaultPageSubgraph,
  formatPercentageBN,
  formatDollarsBN,
} from '@sc1/common';
import { useChain } from '@sc1/common/hooks';
import { ShortenedAddress } from '../ShortenedAddress';
import type { CardProps } from 'theme-ui';

interface AdminInfoProps extends CardProps {
  isVaultManager: boolean | undefined;
  isProposer: boolean | undefined;
  isExecutor: boolean | undefined;
  isTimelockAdmin: boolean | undefined;
}

export const AdminInfo = ({
  isVaultManager,
  isProposer,
  isExecutor,
  isTimelockAdmin,
  ...rest
}: AdminInfoProps): JSX.Element => {
  const { loading, data } = useVaultPageSubgraph();
  const {
    name,
    id,
    owner,
    feePercent,
    underlying,
    underlyingSymbol,
    tvl,
    decimals,
    maxTvl,
    lastHarvest,
    harvestWindow,
    harvestDelay,
    targetFloatPercent,
  } = data?.vault || {};
  const { address: timelockAddress, minDelay } = data?.timelocks[0] || {};
  const customName = getCustomVaultName(name);
  const chain = useChain();
  return (
    <Card {...rest}>
      <Loading loading={loading}>
        <Heading as="h2">{customName}</Heading>
        <Stats data={data?.vault} chain={chain} />
        <AdminBadges
          my={3}
          isVaultManager={isVaultManager}
          isProposer={isProposer}
          isExecutor={isExecutor}
          isTimelockAdmin={isTimelockAdmin}
        />
        <Table
          columns={[1, 2]}
          index={false}
          data={[
            ['Vault Contract', <ShortenedAddress key={id} address={id} />],
            [
              'Underlying Asset',
              <ShortenedAddress
                key={underlying}
                displayString={underlyingSymbol}
                address={underlying}
              />,
            ],
            ['Vault Owner', <ShortenedAddress key={id} address={owner} />],
            [
              'Timelock Contract',
              <ShortenedAddress key={id} address={timelockAddress} />,
            ],
            ['Timelock Delay', secDuration(Number(minDelay))],
            ['Fee Percent', formatPercentageBN(feePercent, 18)],
            ['TVL', formatDollarsBN(tvl, decimals, false)],
            [
              'Max TVL',
              <Text key={'maxtvl'} sx={{ wordWrap: 'break-word' }}>
                {formatDollarsBN(maxTvl, decimals, false)}
              </Text>,
            ],
            ['Last Harvest', fromNow(Number(lastHarvest) * 1000)],
            ['Harvest Window', secDuration(Number(harvestWindow))],
            ['Harvest Delay', secDuration(Number(harvestDelay))],
            [
              'Target Float Percent',
              formatPercentageBN(targetFloatPercent, 18),
            ],
          ]}
        />
      </Loading>
    </Card>
  );
};
